import { useMemo, useState } from 'react';
import { Modal } from '@/components/dialogs/Modal';
import { Select2 } from '@/components/forms/Select2';
import { useToast } from '@/components/common/toast';
import { ApiError } from '@/api/client/abpError';
import { SPREAD_BET } from '@/api/types/globalSymbols';
import type {
  CreateGlobalSymbolDto,
  GlobalSymbolFormDataDto,
  PendingSymbolRequestDto,
} from '@/api/types/globalSymbols';
import {
  useApproveAllSymbolRequests,
  useApproveSymbolRequest,
  useRejectSymbolRequest,
} from './api';

function toInt(value: string | number | null | undefined): number {
  if (value === null || value === undefined || value === '') return 0;
  const n = parseInt(String(value).replace(/,/g, ''), 10);
  return Number.isNaN(n) ? 0 : n;
}

const NA = 'N/A';

// Editable, in-memory copy of a pending request row.
interface RowState {
  requestId: string;
  symbol: string;
  clientName: string;
  connectorName: string;
  type: string;
  typeName: string;
  sbUnderlyingCurrency: string;
  assetClass: string;
  baseCurrency: string;
  quoteCurrency: string;
  quoteAssetClass: string;
  countryName: string; // '' meaning N/A
  contractSize: string;
  digits: string;
}

interface PendingRequestsModalProps {
  show: boolean;
  requests: PendingSymbolRequestDto[];
  formData: GlobalSymbolFormDataDto;
  canApprove: boolean;
  canReject: boolean;
  onClose: () => void;
}

export function PendingRequestsModal({
  show,
  requests,
  formData,
  canApprove,
  canReject,
  onClose,
}: PendingRequestsModalProps) {
  const toast = useToast();
  const approveMut = useApproveSymbolRequest();
  const approveAllMut = useApproveAllSymbolRequests();
  const rejectMut = useRejectSymbolRequest();

  const typeNameById = useMemo(() => {
    const map = new Map<number, string>();
    formData.symbolTypeList.forEach((t) => map.set(t.id, t.name));
    return map;
  }, [formData.symbolTypeList]);

  const typeIdByName = useMemo(() => {
    const map = new Map<string, number>();
    formData.symbolTypeList.forEach((t) => map.set(t.name, t.id));
    return map;
  }, [formData.symbolTypeList]);

  const typeNames = formData.symbolTypeList.map((t) => t.name);
  const assetClasses = formData.assetClassList;
  const countries = useMemo(() => [NA, ...formData.countryList], [formData.countryList]);

  const [rows, setRows] = useState<RowState[]>(() =>
    requests.map((r) => ({
      requestId: r.id,
      symbol: r.symbol,
      clientName: r.clientName,
      connectorName: r.connectorName,
      type: r.type,
      typeName: typeNameById.get(r.typeId) ?? '',
      sbUnderlyingCurrency: r.sbUnderlyingCurrency ?? '',
      assetClass: r.assetClass ?? '',
      baseCurrency: r.baseCurrency ?? '',
      quoteCurrency: r.quoteCurrency ?? '',
      quoteAssetClass: r.quoteAssetClass ?? '',
      countryName: r.countryName ? r.countryName : '',
      contractSize: r.contractSize != null ? String(r.contractSize) : '',
      digits: r.digits != null ? String(r.digits) : '',
    })),
  );

  function updateRow(requestId: string, patch: Partial<RowState>) {
    setRows((prev) => prev.map((r) => (r.requestId === requestId ? { ...r, ...patch } : r)));
  }

  function removeRow(requestId: string) {
    setRows((prev) => {
      const next = prev.filter((r) => r.requestId !== requestId);
      if (next.length === 0) onClose();
      return next;
    });
  }

  function toDto(row: RowState): CreateGlobalSymbolDto {
    const isSpreadBet = row.typeName === SPREAD_BET;
    return {
      symbol: row.symbol,
      typeId: typeIdByName.get(row.typeName) ?? 0,
      sbUnderlyingCurrency: isSpreadBet ? row.sbUnderlyingCurrency : '',
      assetClass: row.assetClass,
      baseCurrency: row.baseCurrency,
      quoteCurrency: row.quoteCurrency,
      quoteAssetClass: row.quoteAssetClass,
      countryName: row.countryName === NA ? '' : row.countryName,
      contractSize: toInt(row.contractSize),
      digits: toInt(row.digits),
    };
  }

  function reportError(err: unknown, fallback: string) {
    toast.error(err instanceof ApiError ? err.message : fallback);
  }

  function handleApprove(row: RowState) {
    approveMut.mutate(
      { requestId: row.requestId, input: toDto(row) },
      {
        onSuccess: () => {
          toast.success('Request approved.');
          removeRow(row.requestId);
        },
        onError: (err) => reportError(err, 'Failed to approve request.'),
      },
    );
  }

  function handleReject(row: RowState) {
    rejectMut.mutate(row.requestId, {
      onSuccess: () => {
        toast.success('Request rejected.');
        removeRow(row.requestId);
      },
      onError: (err) => reportError(err, 'Failed to reject request.'),
    });
  }

  function handleApproveAll() {
    if (rows.length === 0) return;
    const payload = rows.map((r) => ({ requestId: r.requestId, globalSymbol: toDto(r) }));
    approveAllMut.mutate(payload, {
      onSuccess: (result) => {
        const failedNames = result.errors.map((e) => e.split(':')[0].trim());
        const stillPending = rows.filter((r) => failedNames.includes(r.symbol));
        const approvedCount = rows.length - stillPending.length;
        setRows(stillPending);

        if (failedNames.length > 0) {
          toast.error(
            `${approvedCount} approved. ${failedNames.length} still need required fields — fill them and Approve All again.`,
          );
        } else {
          toast.success(`${approvedCount} approved.`);
          onClose();
        }
      },
      onError: (err) => reportError(err, 'Failed to approve requests.'),
    });
  }

  const busy = approveMut.isPending || approveAllMut.isPending || rejectMut.isPending;

  return (
    <Modal
      show={show}
      title="Pending Symbol Requests"
      onClose={onClose}
      size="xl"
      footer={
        <>
          <button type="button" className="btn btn-light" onClick={onClose} disabled={busy}>
            Close
          </button>
          {canApprove && (
            <button type="button" className="btn btn-primary" onClick={handleApproveAll} disabled={busy}>
              Approve All
            </button>
          )}
        </>
      }
    >
      <div className="table-responsive text-start" style={{ maxHeight: '60vh' }}>
        <table className="table dataTable table-borderless align-middle text-nowrap text-center">
          <thead>
            <tr>
              <th>Instrument</th>
              <th>Instrument Type</th>
              <th>SB Underlying Currency</th>
              <th>Asset Class</th>
              <th>Base</th>
              <th>Quote</th>
              <th>Quote Asset Class</th>
              <th>Country</th>
              <th>Contract Size</th>
              <th>Digits</th>
              <th>Client</th>
              <th>Connector</th>
              <th>Request Type</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const sbEnabled = row.typeName === SPREAD_BET;
              return (
                <tr key={row.requestId}>
                  <td>{row.symbol}</td>
                  <td>
                    <Select2
                     
                      value={row.typeName}
                      onChange={(v) =>
                        updateRow(row.requestId, {
                          typeName: v ?? '',
                          sbUnderlyingCurrency:
                            v === SPREAD_BET ? row.sbUnderlyingCurrency : '',
                        })
                      }
                      options={typeNames.map((n) => ({ value: n, label: n }))}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      disabled={!sbEnabled}
                      value={sbEnabled ? row.sbUnderlyingCurrency : ''}
                      onChange={(e) => updateRow(row.requestId, { sbUnderlyingCurrency: e.target.value })}
                    />
                  </td>
                  <td>
                    <Select2
                     
                      value={row.assetClass}
                      onChange={(v) => updateRow(row.requestId, { assetClass: v ?? '' })}
                      options={assetClasses.map((a) => ({ value: a, label: a }))}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={row.baseCurrency}
                      onChange={(e) => updateRow(row.requestId, { baseCurrency: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={row.quoteCurrency}
                      onChange={(e) => updateRow(row.requestId, { quoteCurrency: e.target.value })}
                    />
                  </td>
                  <td>
                    <Select2
                     
                      value={row.quoteAssetClass}
                      onChange={(v) => updateRow(row.requestId, { quoteAssetClass: v ?? '' })}
                      options={assetClasses.map((a) => ({ value: a, label: a }))}
                    />
                  </td>
                  <td>
                    <Select2
                     
                      allowClear={false}
                      value={row.countryName === '' ? NA : row.countryName}
                      onChange={(v) =>
                        updateRow(row.requestId, {
                          countryName: v === NA || v == null ? '' : v,
                        })
                      }
                      options={countries.map((c) => ({ value: c, label: c }))}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={row.contractSize}
                      onChange={(e) => updateRow(row.requestId, { contractSize: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={row.digits}
                      onChange={(e) => updateRow(row.requestId, { digits: e.target.value })}
                    />
                  </td>
                  <td>{row.clientName}</td>
                  <td>{row.connectorName}</td>
                  <td>{row.type}</td>
                  <td>
                    <div className="d-inline-flex gap-1">
                      {canApprove && (
                        <button
                          type="button"
                          className="btn btn-xs btn-outline-success"
                          onClick={() => handleApprove(row)}
                          disabled={busy}
                        >
                          Approve
                        </button>
                      )}
                      {canReject && (
                        <button
                          type="button"
                          className="btn btn-xs btn-outline-danger"
                          onClick={() => handleReject(row)}
                          disabled={busy}
                        >
                          Reject
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}
